import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

import { type IconSet } from '../../icons';
import { type AriaOptions } from '../../interfaces';
import { IconButtonSize, IconButtonTheme } from './enums';
import { ButtonType } from '../button';

@Component({
  selector: 'fcom-icon-button',
  templateUrl: './icon-button.component.html',
  styleUrls: ['./icon-button.component.scss', '../buttons-shared-styles.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class IconButtonComponent {
  /**
   * The size of the button, currently 3 static sizes
   */
  size = input<IconButtonSize>(IconButtonSize.MEDIUM);

  /**
   * There are 8 themes available right now that renders different backgrounds, font colors and borders
   */
  theme = input<IconButtonTheme>(IconButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE);

  /**
   * An icon to include, with its category and name
   */
  icon = input.required<IconSet>();

  /**
   * The aria tags that must be added to a button or link
   */
  aria = input.required<AriaOptions>();

  /**
   * Whether the button should be disabled or not
   */
  disabled = input<boolean>(false);

  /**
   * The pre-translated hover text to display
   */
  title = input<string | undefined>(undefined);

  /**
   * If button should render as an a tag, then pass in the url
   */
  href = input<string | undefined>(undefined);

  /**
   * If link, should it open in a new tab
   */
  openInNewTab = input<boolean | undefined>(undefined);

  /**
   * If link, is it external
   */
  externalLink = input<boolean | undefined>(undefined);

  /**
   * The type of button, for example "button" or "submit"
   */
  type = input<ButtonType>(ButtonType.BUTTON);

  clickBtn = output<Event>();

  /**
   * A data-testid that is passed on to the `<button>`
   */
  dataTestId = input<string | undefined>(undefined);

  public readonly IconButtonSize = IconButtonSize;
  public readonly IconButtonTheme = IconButtonTheme;

  onClick(event: Event): void {
    if (this.disabled() || this.aria().disabled) {
      event.preventDefault();
      return;
    }

    this.clickBtn.emit(event);
  }
}
